///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../../helpers/messages";
import CloseIcon from "../../icons/CloseIcon";
import Check from "../../icons/Check";
import {
  addNewCarrierMatches,
  clearCarriersSelected,
  createLaneMatches,
  setCarriersSelected,
  setNoneOption
} from "../../../slices/bidProcurementSlice";
import AutoComplete from "../../form-controls/AutoComplete";
import { getCarriers } from "../../../api/carrier";

export const CarrierMatchInformation = () => {
  const dispatch = useDispatch();
  const {
    bidProcurement: {
      rowsSelected,
      capacityMatchCarriers: { carriers, noneOption }
    },
    featureFlags: { useV2CarrierSearch, showSlicCodes }
  } = useSelector(state => state);
  const [showCarrierSearchDropdown, setShowCarrierSearchDropdown] =
    useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(null);

  useEffect(() => {
    dispatch(createLaneMatches({ truckloadLaneIds: rowsSelected, carriers }));
  }, [dispatch, rowsSelected, carriers]);

  const HeaderCell = ({ columnName, children, onClick, className }) => {
    return (
      <th onClick={onClick} className={className}>
        {columnName || children}
      </th>
    );
  };

  const BodyCell = ({ value, children, maxWidth, minWidth, className }) => (
    <td
      className={`body-cell ${className}`}
      style={{ maxWidth: maxWidth, minWidth: minWidth }}
    >
      {value || children}
    </td>
  );

  return (
    <div onClick={() => setShowCarrierSearchDropdown(false)}>
      <div className="mt-48 pt-12 pl-24">
        <span className="font-size-18 weight--semibold">
          {messages.matches.defaultMessage}{" "}
        </span>
        <span className="match-cell m-0 p-0 align-text-bottom ml-8">
          {carriers?.length ?? 0}
        </span>
        <p className="pt-8">
          {
            messages.selectAnyMatchesYouWouldLikeToApplyToThisLane
              .defaultMessage
          }
        </p>
      </div>
      <div className="mt-40 pl-8" style={{ minHeight: "392px" }}>
        <table className="carrier-info">
          <thead>
            <tr>
              <HeaderCell columnName={messages.carrierName.defaultMessage} />
              <HeaderCell
                columnName={
                  showSlicCodes
                    ? messages.carrierInfo.defaultMessage
                    : messages.incumbentCarrier.defaultMessage
                }
                className={showSlicCodes ? "flex justify-center" : ""}
              />
              <HeaderCell
                onClick={e => {
                  e.stopPropagation();
                  setShowCarrierSearchDropdown(true);
                }}
              >
                <CloseIcon
                  height={14}
                  width={14}
                  fill={showCarrierSearchDropdown ? "#adafb2" : "#0066FA"}
                  style={{
                    transform: `rotate(45deg)`,
                    verticalAlign: "text-bottom",
                    cursor: `${showCarrierSearchDropdown ? "" : "pointer"}`
                  }}
                  title={messages.add.defaultMessage}
                />
                <span
                  className={`text-cobalt-blue text-transform-none weight--normal pl-8 ${
                    showCarrierSearchDropdown
                      ? "text-light-grey"
                      : "cursor-pointer"
                  }`}
                >
                  {messages.addCapSource.defaultMessage}
                </span>
              </HeaderCell>
            </tr>
          </thead>
          <tbody>
            {showCarrierSearchDropdown ? (
              <div
                className="pl-16 pt-8 cap-source"
                onClick={e => e.stopPropagation()}
              >
                <AutoComplete
                  fetchFrom={getCarriers}
                  handleOptionsMapping={({ carrierDetails }) =>
                    carrierDetails.map(option => ({
                      ...option,
                      label: `${option.carrierName} (${option.carrierCode})`,
                      value: option.id
                    }))
                  }
                  value={selectedCarrier}
                  label={messages.newCarrierName.defaultMessage}
                  handleChange={selectedCarrier =>
                    setSelectedCarrier(selectedCarrier)
                  }
                  filterDynamicOptions={useV2CarrierSearch ? false : true}
                  placeholder={
                    useV2CarrierSearch
                      ? messages.carrierSearchPlaceholder.defaultMessage
                      : ""
                  }
                />
                <CloseIcon
                  height={16}
                  width={16}
                  fill="#C71E0C"
                  className="pos-relative cursor-pointer"
                  style={{ top: "-47px", left: "472px" }}
                  onClick={() => setSelectedCarrier(null)}
                />
                <Check
                  height={24}
                  width={24}
                  fill="#137C00"
                  className="pos-relative cursor-pointer"
                  style={{ top: "-44px", left: "472px" }}
                  onClick={() => {
                    selectedCarrier &&
                      dispatch(addNewCarrierMatches(selectedCarrier));
                    setSelectedCarrier(null);
                    dispatch(setNoneOption(false));
                  }}
                />
              </div>
            ) : null}
            <tr>
              <BodyCell minWidth={260}>
                <input
                  id="none-radio"
                  type="radio"
                  checked={noneOption}
                  onChange={() => {
                    dispatch(clearCarriersSelected());
                    dispatch(setNoneOption(true));
                  }}
                />
                <span className="align-text-bottom pl-4">
                  {messages.none.defaultMessage}
                </span>
              </BodyCell>
              <BodyCell minWidth={150} />
              <BodyCell minWidth={170} />
            </tr>
            {carriers?.map((x, index) => {
              return (
                <tr key={index}>
                  <BodyCell maxWidth={260}>
                    <input
                      type="checkbox"
                      checked={x.checked}
                      onChange={() => {
                        dispatch(setNoneOption(false));
                        dispatch(setCarriersSelected(x.carrierId));
                      }}
                    />
                    <span
                      className="align-text-bottom pl-4"
                      title={`${x.carrierName} (${x.carrierCode})`}
                    >
                      {x.carrierName} ({x.carrierCode})
                    </span>
                    {showSlicCodes && x.slicLanes?.length > 0 ? (
                      <div title={x.slicLanes.join(", ")}>
                        <span className="text-grayish-blue ml-24 font-bold">
                          {messages.slicLanes.defaultMessage}
                        </span>{" "}
                        {x.slicLanes.join(", ")}
                      </div>
                    ) : null}
                  </BodyCell>
                  <BodyCell maxWidth={150} className="flex justify-center">
                    <div>
                      {x.incumbent ? (
                        <span className="carriers-cell mb-8">
                          {messages.incumbent.defaultMessage}
                        </span>
                      ) : null}
                      {showSlicCodes && x.slicLanes?.length > 0 ? (
                        <span
                          className="carriers-cell ups-tag"
                          style={{ width: 45 }}
                        >
                          {messages.ups.defaultMessage}
                        </span>
                      ) : null}
                    </div>
                  </BodyCell>
                  <BodyCell maxWidth={170} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CarrierMatchInformation;
